import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useT } from "@/i18n";

// Churn-feedback modal shown right before we call DELETE /api/auth/me.
// The reasons are stable slugs (NOT translated strings) so the backend
// aggregate stays consistent across languages. Labels are translated.
const REASON_SLUGS = [
  { slug: "too_expensive", en: "Too expensive", tr: "Çok pahalı" },
  { slug: "not_useful", en: "Not useful enough for me", tr: "Bana yeterli fayda sağlamıyor" },
  { slug: "missing_feature", en: "Missing a feature I need", tr: "İhtiyacım olan özellik yok" },
  { slug: "bugs", en: "Too many bugs / technical issues", tr: "Çok fazla hata / teknik sorun" },
  { slug: "found_alternative", en: "Found a better alternative", tr: "Daha iyi bir alternatif buldum" },
  { slug: "privacy", en: "Privacy concerns", tr: "Gizlilik endişeleri" },
  { slug: "just_trying", en: "Was just trying it out", tr: "Sadece deniyordum" },
  { slug: "other", en: "Other reason", tr: "Diğer" },
];

export default function DeleteAccountDialog({ open, onOpenChange, onSuccess }) {
  const { t, lang } = useT();
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isTR = (lang || "en").toLowerCase().startsWith("tr");

  const submit = async () => {
    // Silme her koşulda gerçekleşir; feedback opsiyoneldir. Kullanıcı
    // hiçbir şey seçmese bile Continue → doğrudan DELETE'e gider.
    setSubmitting(true);
    try {
      await api.delete("/auth/me", {
        data: {
          reason: reason || null,
          comment: (comment || "").trim() || null,
        },
      });
      onSuccess?.();
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
          t("delete_account_err") ||
          "Could not delete account. Please try again or contact support."
      );
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            {isTR ? "Hesabını silmeden önce…" : "Before you delete your account…"}
          </DialogTitle>
          <DialogDescription>
            {isTR
              ? "Bize neden ayrıldığını söylersen çok değerli — daha iyi bir Lumioos yapmamıza yardım edersin. Bu adım tamamen opsiyonel."
              : "Telling us why helps us build a better Lumioos. This step is completely optional."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[50vh] overflow-y-auto">
          <div className="space-y-1.5">
            {REASON_SLUGS.map((r) => {
              const label = isTR ? r.tr : r.en;
              const active = reason === r.slug;
              return (
                <button
                  key={r.slug}
                  type="button"
                  onClick={() => setReason(active ? "" : r.slug)}
                  data-testid={`delete-reason-${r.slug}`}
                  className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                    active
                      ? "border-red-500 bg-red-50 text-red-900 font-semibold"
                      : "border-zinc-200 hover:border-zinc-400 text-zinc-700"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">
              {isTR ? "Eklemek istediğin başka bir şey? (opsiyonel)" : "Anything else you'd like to add? (optional)"}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
              rows={3}
              data-testid="delete-comment-input"
              placeholder={
                isTR
                  ? "Örn: Hangi özellik olsaydı kalırdım…"
                  : "e.g. What would have made you stay…"
              }
              className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
            />
          </div>

          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2.5 leading-relaxed">
            {isTR ? (
              <>
                ⚠️ Devam edersen hesabın ve <strong>tüm verilerin</strong>{" "}
                (sohbetler, videolar, görseller, CV, abonelik) <strong>kalıcı olarak silinecek</strong>. Bu işlem geri alınamaz.
              </>
            ) : (
              <>
                ⚠️ If you continue, your account and <strong>all your data</strong> (chats, videos, images, CV, subscription) <strong>will be permanently deleted</strong>. This action cannot be undone.
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="delete-cancel-btn"
            className="px-4 py-2 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            {isTR ? "Vazgeç, kalıyorum" : "Cancel, I'm staying"}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="delete-confirm-btn"
            className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {isTR
              ? submitting ? "Siliniyor…" : "Hesabımı kalıcı olarak sil"
              : submitting ? "Deleting…" : "Permanently delete my account"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
